import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowDown, Calendar, AlertCircle } from 'lucide-react';
import { api } from '@/api';
import LocaleSwitch from '@shared/ui/LocaleSwitch.jsx';
import EventAbout from '@shared/ui/EventAbout.jsx';
import EventHero from '@shared/ui/EventHero.jsx';
import PartnerFooter from '@shared/ui/PartnerFooter.jsx';
import { formatEventDate, formatEventDateShort } from '@shared/ui/event-date.js';
import RegisterForm from '@/components/RegisterForm';

const LOGO = '/logos/logo.svg';
const TICKER = 'BOOK YOUR MONDAY · ';

/**
 * The microsite is a registration funnel, nothing more.
 * Programme, speakers and everything else live in the app — duplicating them
 * here would mean two places to keep correct as the agenda changes.
 */
export default function Landing() {
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [blocks, setBlocks] = useState({});
  const [providers, setProviders] = useState([]);
  const [pendingSignup, setPendingSignup] = useState(null);
  const [existingTicket, setExistingTicket] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // The blocks were never fetched here, which is why the copy the client
    // keeps revising appeared in the app and nowhere on the web.
    api.event
      .content()
      .then((res) => {
        if (!cancelled) setBlocks(res?.byKey || {});
      })
      .catch((err) => console.error('[landing] content failed', err));

    api.event
      .settings()
      .then((data) => {
        if (!cancelled) setEvent(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage]);

  // Which social buttons to offer. Fetched once — it does not vary by language.
  useEffect(() => {
    api
      .request('/auth/oauth/providers', { auth: false })
      .then((r) => setProviders(r?.providers || []))
      .catch(() => setProviders([]));
  }, []);

  /*
   * Returning from a social provider.
   *
   * The callback hands back a one-time ticket rather than tokens, so nothing
   * sensitive is in the URL — but the ticket still gets stripped from the
   * address bar immediately so a copied link cannot replay the sign-in.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('auth_ticket');
    const failure = params.get('auth_error');

    if (!ticket && !failure) return;

    const clean = new URL(window.location.href);
    clean.searchParams.delete('auth_ticket');
    clean.searchParams.delete('auth_error');
    clean.searchParams.delete('signup');
    window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);

    if (failure) {
      setAuthError(
        {
            cancelled: t('social.cancelled'),
            no_email: t('social.noEmail'),
            state_expired: t('social.expired'),
            email_unverified: t('social.emailUnverified'),
          }[failure] ||
          t('social.failed')
      );
      return;
    }

    api
      .request('/auth/oauth/exchange', { method: 'POST', auth: false, body: { ticket } })
      .then(async (result) => {
        if (result.status === 'signup_required') {
          setPendingSignup({ ticket, ...result });
        } else if (result.status === 'signed_in') {
          /*
           * Already registered. Show them the ticket they came back for.
           *
           * This used to set the tokens and scroll to #register, which renders
           * the empty registration form — so signing in with a provider you had
           * already used looked like the sign-in had simply failed, and invited
           * a second registration with the same email.
           */
          await api.setTokens(result);
          setPendingSignup(null);
          setAuthError(null);
          setExistingTicket({
            email: result.participant?.email,
            ticket_code: result.participant?.ticket_code,
            returning: true,
          });
          document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' });
        }
      })
      .catch(() => setAuthError(t('social.failed')));
  }, [t]);

  const registrationOpen = event?.registration_open !== false;
  const hasStoreLinks = Boolean(event?.store?.ios || event?.store?.android);

  const eventDate = formatEventDate(event?.event_date, i18n.resolvedLanguage);
  const eventDateShort = formatEventDateShort(event?.event_date);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Masthead ───────────────────────────────────────────── */}
      {/*
        The same component the app's home screen uses, so the two cannot drift
        into looking like different products again. The date line and the
        scroll-to-register button are passed in: only the web needs them. The
        venue is not — the hero owns that for both surfaces.
      */}
      <EventHero event={event}>
        <dl className="flex flex-wrap gap-x-6 gap-y-2 mt-6">
          {eventDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-brand shrink-0" aria-hidden="true" />
              <dd className="text-white/70 text-sm font-body">{eventDate}</dd>
            </div>
          )}
          {/*
            No venue here: EventHero renders it under the headline now, for both
            surfaces. Leaving this in showed the web visitor the place twice.
          */}
        </dl>

        <a
          href="#register"
          className="inline-flex items-center gap-2 mt-8 bg-brand text-black font-heading font-black uppercase
                     text-lg px-6 py-3.5 rounded-xl hover:brightness-95 transition"
        >
          {t('microsite.heroCta')} <ArrowDown className="w-5 h-5" />
        </a>
      </EventHero>


      {/* ── About the event ────────────────────────────────────── */}
      <EventAbout blocks={blocks} large className="site-shell px-5 pt-12 w-full" />

      {/* ── Register ───────────────────────────────────────────── */}
      <section id="register" className="site-shell px-5 pt-12 scroll-mt-20 w-full">
        <h2 className="display-lg text-white text-4xl mb-2">{t('microsite.registerTitle')}</h2>
        <p className="text-white/40 text-sm font-body mb-5">{t('microsite.registerSubtitle')}</p>
        {authError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 px-3 py-2.5 mb-4"
          >
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-destructive text-xs font-body">{authError}</p>
          </div>
        )}
        {loading ? (
          <div className="h-96 rounded-2xl bg-white/5 animate-pulse" />
        ) : (
          <RegisterForm
            open={registrationOpen}
            event={event}
            providers={providers}
            pendingSignup={pendingSignup}
            existingTicket={existingTicket}
          />
        )}
      </section>

      {/* ── App ────────────────────────────────────────────────── */}
      <section className="site-shell px-5 pt-10 w-full">
        <div className="bg-brand rounded-2xl p-6">
          <h2 className="font-heading font-black uppercase text-black text-2xl leading-tight">
            {t('microsite.appTitle')}
          </h2>
          <p className="text-black/70 text-sm font-body mt-2">{t('microsite.appBody')}</p>

          {/*
            The official store badges, which people recognise far faster than a
            styled text button. Both always show, so the card reads as a real
            destination while the listings are still in review; until the CMS
            carries the real URLs they point at '#', and the note below stays up
            rather than letting a dead button imply a download is waiting.

            Both files are 1084x327, so a shared height lines them up exactly —
            Apple and Google publish these at different native ratios, and the
            usual result is one badge sitting a few pixels taller than the other.
          */}
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <a href={event?.store?.ios || '#'} aria-label={t('microsite.appStoreLabel')}>
              <img
                src="/buttons/app-store.png"
                alt={t('microsite.appStoreLabel')}
                className="h-11 w-auto hover:opacity-85 transition"
              />
            </a>
            <a href={event?.store?.android || '#'} aria-label={t('microsite.googlePlayLabel')}>
              <img
                src="/buttons/google-play.png"
                alt={t('microsite.googlePlayLabel')}
                className="h-11 w-auto hover:opacity-85 transition"
              />
            </a>
          </div>

          {!hasStoreLinks && (
            <p className="label-tag text-black/50 text-[10px] mt-3">{t('microsite.appComingSoon')}</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="site-shell px-5 pt-14 pb-10 mt-auto w-full">
      <PartnerFooter api={api} className="mb-8" />
      <div className="h-px bg-white/10 mb-5" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-white/30 text-xs font-body">
          © {new Date().getFullYear()} {t('microsite.organiser')}. {t('microsite.rights')}
        </p>
        <a href="/privacy" className="text-white/40 hover:text-white text-xs font-body underline underline-offset-2">
          {t('microsite.privacyLink')}
        </a>
      </div>
    </footer>
  );
}
